import { Observable } from "rxjs";
import { inject, injectable } from "tsyringe";
import { autoBind } from "src/shared/decorators/autobind";
import { HttpClient } from "src/infra/http/client";
import { replaceString } from "src/libs/replace-string";
import { PgaPolicy } from "src/shared/models";

enum PolicyDetailEndpoints {
    Detail = "insureds/:userIdentity/insurances/:businessLine/policies/:policyId",
    Client = "insureds/:userIdentity/insurances/all/policies/:policyId/clients/:clientId",
    Insureds = "insureds/:userIdentity/insurances/all/policies/:policyId/insureds",
    Beneficiaries = "insureds/:userIdentity/insurances/all/policies/:policyId/beneficiaries",
}

// TODO - this has to be moved to EPS folder once it exists
enum EpsDetailEndpoints {
    Detail = "insureds/:userIdentity/insurances/eps/plans/:policyId",
    Insureds = "insureds/:userIdentity/insurances/eps/plans/all/insureds",
}

export type PolicyClientPayload = {
    policyId: number;
    userIdentity: string;
    systemCode: string;
    clientId: number | string;
    businessLine: string;
};

export type PolicyDetailPayload = {
    policyId: number;
    userIdentity: string;
    systemCode: string;
    businessLine: string;
};

@injectable()
export class PolicyDetailRepository {
    constructor(@inject("HttpClient") private http: HttpClient) {}

    @autoBind
    getDetail(payload: PolicyDetailPayload): Observable<object> {
        const { policyId, userIdentity, businessLine, systemCode } = payload;
        const headers = this.http.httpUtils.getMsBaseHeaders(
            userIdentity,
            "MEP"
        );
        const baseUrl =
            businessLine === "plans"
                ? EpsDetailEndpoints.Detail
                : PolicyDetailEndpoints.Detail;
        const url = replaceString(baseUrl, {
            ":policyId": policyId,
            ":businessLine": businessLine,
            ":userIdentity": userIdentity,
        });
        return this.http.getJSON<object>(url, {
            isPublicRequest: false,
            params: { "system-code": systemCode },
            customHeader: {
                ...this.http.httpUtils.getBaseHeaders(),
                ...headers,
            },
        });
    }

    @autoBind
    getClient(payload: PolicyClientPayload): Observable<object> {
        const { policyId, userIdentity, businessLine, systemCode, clientId } =
            payload;
        const headers = this.http.httpUtils.getMsBaseHeaders(
            userIdentity,
            "MEP"
        );
        const url = replaceString(PolicyDetailEndpoints.Client, {
            ":policyId": policyId,
            ":businessLine": businessLine,
            ":userIdentity": userIdentity,
            ":clientId": clientId,
        });

        return this.http.getJSON<object>(url, {
            isPublicRequest: false,
            params: { "system-code": systemCode },
            customHeader: {
                ...this.http.httpUtils.getBaseHeaders(),
                ...headers,
            },
        });
    }

    @autoBind
    getInsureds(payload: PolicyDetailPayload): Observable<Array<object>> {
        const { policyId, userIdentity, businessLine, systemCode } = payload;
        const headers = this.http.httpUtils.getMsBaseHeaders(
            userIdentity,
            "MEP"
        );
        const endpoint =
            businessLine === "plans"
                ? EpsDetailEndpoints.Insureds
                : PolicyDetailEndpoints.Insureds;
        const url = replaceString(endpoint, {
            ":userIdentity": userIdentity,
            ":policyId": policyId,
        });
        // `policyId` is actually `insuredCode` for EPS plans
        const params =
            businessLine === "plans"
                ? { "insured-code": policyId.toString() }
                : { "system-code": systemCode };
        return this.http.getJSON<object[]>(url, {
            isPublicRequest: false,
            params,
            customHeader: {
                ...this.http.httpUtils.getBaseHeaders(),
                ...headers,
            },
        });
    }

    @autoBind
    getBeneficiaries(
        userIdentity: string,
        policy: PgaPolicy
    ): Observable<Array<object>> {
        const url = replaceString(PolicyDetailEndpoints.Beneficiaries, {
            ":userIdentity": userIdentity,
            ":policyId": policy.id,
        });
        const headers = this.http.httpUtils.getMsBaseHeaders(
            userIdentity,
            "MEP"
        );
        return this.http.getJSON<object[]>(url, {
            isPublicRequest: false,
            params: { "system-code": policy.product.systemCode },
            customHeader: {
                ...this.http.httpUtils.getBaseHeaders(),
                ...headers,
            },
        });
    }
}
